import * as Yup from 'yup'
import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
// @mui
import LoadingButton from '@mui/lab/LoadingButton'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Unstable_Grid2'
import Typography from '@mui/material/Typography'
// utils
import { fData } from 'src/utils/format-number'
// components
import { useSnackbar } from 'src/components/snackbar'
import FormProvider, { RHFTextField, RHFUploadAvatar } from 'src/components/hook-form'
import { useUserStore } from 'src/store/user'
import axios, { API_ENDPOINTS } from 'src/utils/axios'
import { IUserAccount } from 'src/types/user'
import Iconify from 'src/components/iconify'

const ROLE_LABELS: Record<string, { label: string; color: string }> = {
  admin: { label: 'Administrador', color: 'primary.main' },
  franchisee: { label: 'Franqueado', color: 'info.main' },
  user: { label: 'Usuário', color: 'success.main' },
}

export default function AccountGeneral() {
  const { enqueueSnackbar } = useSnackbar()

  const { user, setUser } = useUserStore()

  const UpdateUserSchema = Yup.object().shape({
    name: Yup.string().required('Nome é obrigatório'),
    email: Yup.string()
      .required('Email é obrigatório')
      .email('O e-mail deve ser um endereço de e-mail válido'),
    photo: Yup.mixed().nullable(),
  })

  const defaultValues = {
    name: user?.name || '',
    email: user?.email || '',
    photo: user?.photo || null,
  }

  const methods = useForm<IUserAccount>({
    resolver: yupResolver(UpdateUserSchema),
    defaultValues,
  })

  const {
    setValue,
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = methods

  const onSubmit = useCallback(
    async (data: IUserAccount) => {
      try {
        const { data: response } = await axios.put(API_ENDPOINTS.user.editMe, {
          name: data.name,
        })

        setUser(response)

        if (typeof data.photo !== 'string' && data.photo) {
          const formDataUpload: FormData = new FormData()
          formDataUpload.append('photo', data.photo)
          try {
            await axios.put(API_ENDPOINTS.user.editPhoto, formDataUpload)
            enqueueSnackbar('Foto de perfil atualizada com sucesso!')
          } catch (error) {
            enqueueSnackbar('Erro ao enviar a foto perfil!', { variant: 'error' })
          }
        }

        enqueueSnackbar('Perfil atualizado com sucesso!')
      } catch (error) {
        enqueueSnackbar('Erro ao editar perfil!', { variant: 'error' })
      }
    },
    [enqueueSnackbar, setUser],
  )

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]

      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      })

      if (file) {
        setValue('photo', newFile, { shouldValidate: true, shouldDirty: true })
      }
    },
    [setValue],
  )

  const userRole = (user as any)?.role as string | undefined
  const roleInfo = userRole ? ROLE_LABELS[userRole] : null

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid xs={12} md={4}>
          <Card sx={{ pt: 6, pb: 4, px: 3, textAlign: 'center', borderRadius: 2 }}>
            <RHFUploadAvatar
              name='photo'
              maxSize={3145728}
              onDrop={handleDrop}
              helperText={
                <Typography
                  variant='caption'
                  sx={{
                    mt: 3,
                    mx: 'auto',
                    display: 'block',
                    textAlign: 'center',
                    color: 'text.disabled',
                  }}
                >
                  Permitido *.jpeg, *.jpg, *.png, *.gif
                  <br /> tamanho máximo de {fData(3145728)}
                </Typography>
              }
            />

            {roleInfo && (
              <Chip
                label={roleInfo.label}
                size='small'
                icon={<Iconify icon='solar:shield-user-bold' width={14} />}
                sx={{
                  mt: 3,
                  fontWeight: 600,
                  bgcolor: (theme) => `${theme.palette.primary.lighter}`,
                  color: roleInfo.color,
                }}
              />
            )}

            <Typography variant='subtitle1' fontWeight={700} sx={{ mt: 2 }}>
              {user?.name}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {user?.email}
            </Typography>
          </Card>
        </Grid>

        <Grid xs={12} md={8}>
          <Card sx={{ p: 3, borderRadius: 2 }}>
            <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 3 }}>
              <Iconify icon='solar:user-id-bold-duotone' width={24} color='primary.main' />
              <Typography variant='h6' fontWeight={700}>
                Informações pessoais
              </Typography>
            </Stack>

            <Box
              rowGap={2.5}
              columnGap={2}
              display='grid'
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
              }}
            >
              <RHFTextField name='name' label='Nome completo' />
              <RHFTextField
                name='email'
                label='Email'
                disabled
                helperText='Para alterar o email, entre em contato com o suporte.'
              />
            </Box>

            <Stack
              direction='row'
              spacing={2}
              alignItems='center'
              justifyContent='flex-end'
              sx={{ mt: 4 }}
            >
              <LoadingButton
                type='submit'
                variant='contained'
                size='large'
                disabled={!isDirty}
                loading={isSubmitting}
                startIcon={<Iconify icon='solar:diskette-bold' />}
              >
                Salvar alterações
              </LoadingButton>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  )
}
